import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { createPortal } from 'react-dom'
import { fetchWalletHoldings, fetchWalletGraph, fetchWalletFunding, fetchWalletClusters, fetchWalletActivity, isWalletAddress, openWalletMap } from '../utils/walletSources'

/**
 * WalletMapPanel — any wallet's holdings + interaction map.
 *
 * Opens on the `solcheddar-open-wallet` window event (openWalletMap() in
 * utils/walletSources), fired from the followed list, the tape and the
 * wallet-stats rows. Top: holdings by $ value. Below: a small canvas force
 * layout — the wallet in the centre, coins sized by $ held, co-traders from
 * the 72 h capture (edge thickness = score) and, on request, dashed funding
 * links. Click a coin = chart it; click a wallet = recentre (Back returns).
 *
 * Followed wallets refresh holdings every 5 min while open; others load once
 * per open. All data is server-cached and shared, so the cost is per wallet,
 * not per viewer.
 */

const REFRESH_MS = 5 * 60_000

// ── Client cache: reopening a wallet (or going Back to it) renders instantly.
// address -> { hold, graph, fund: {data, at}, layout: {pos, yaw, pitch}, used }
// Mirrored to sessionStorage (last 20 wallets, size-capped) so it survives the
// panel closing and a soft reload. Stale entries refresh quietly in the
// background at the server cache ages; funding is never auto-fetched.
const CACHE_TTL = { hold: 5 * 60_000, graph: 5 * 60_000, fund: 24 * 3600_000 }   // = server cache ages
const CACHE_KEY = 'solcheddar_walletmap_cache_v1'
const CACHE_MAX = 20
const CACHE_MAX_CHARS = 1_500_000
const WCACHE = new Map()
try {
  const raw = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '[]')
  for (const [k, v] of raw) if (isWalletAddress(k) && v) WCACHE.set(k, v)
} catch {}
let persistTimer = 0
function cachePersist() {
  clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    try {
      let list = [...WCACHE.entries()].sort((a, b) => (b[1].used || 0) - (a[1].used || 0)).slice(0, CACHE_MAX)
      let json = JSON.stringify(list)
      while (json.length > CACHE_MAX_CHARS && list.length > 1) { list = list.slice(0, -1); json = JSON.stringify(list) }
      sessionStorage.setItem(CACHE_KEY, json)
    } catch {}
  }, 400)
}
function cacheEntry(addr) {
  let e = WCACHE.get(addr)
  if (!e) { e = {}; WCACHE.set(addr, e) }
  e.used = Date.now()
  if (WCACHE.size > CACHE_MAX) {
    const oldest = [...WCACHE.entries()].sort((a, b) => (a[1].used || 0) - (b[1].used || 0))[0]
    if (oldest && oldest[0] !== addr) WCACHE.delete(oldest[0])
  }
  return e
}
function cachePut(addr, kind, data) {
  if (!addr) return
  cacheEntry(addr)[kind] = { data, at: Date.now() }
  cachePersist()
}
function cacheFresh(addr, kind) {
  const e = WCACHE.get(addr)?.[kind]
  return e && Date.now() - e.at < CACHE_TTL[kind] ? e : null
}
function cacheAny(addr, kind) { return WCACHE.get(addr)?.[kind] || null }

// Wallet-map glyph: 8 solid dots in four sizes, scattered with no symmetry;
// a hub (centre dot linked to five), the rest floating. Original drawing. currentColor, so CSS drives muted grey / amber
// hover. 14-unit viewBox, sized to read at 12-14px.
const WM_DOTS = [[4.1, 5.0, 1.7], [7.6, 7.3, 1.0], [5.2, 10.6, 1.25], [10.9, 2.6, 0.9],
  [12.2, 6.1, 0.6], [1.6, 12.3, 0.6], [10.6, 11.4, 1.5], [8.3, 1.7, 0.6]]
const WM_LINKS = [[0, 1], [1, 2], [1, 4], [1, 7], [1, 6]]
export function WalletMapIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true" style={{ display: 'block' }}>
      <g stroke="currentColor" strokeWidth="0.9" strokeLinecap="round">
        {WM_LINKS.map(([a, b], i) => (
          <line key={i} x1={WM_DOTS[a][0]} y1={WM_DOTS[a][1]} x2={WM_DOTS[b][0]} y2={WM_DOTS[b][1]} />
        ))}
      </g>
      <g fill="currentColor">
        {WM_DOTS.map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} />)}
      </g>
    </svg>
  )
}
const short = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : '')
function fmtUsd(v) {
  if (v == null || !isFinite(v)) return '—'
  const a = Math.abs(v)
  if (a >= 1e9) return `$${(v / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `$${(v / 1e3).toFixed(1)}K`
  if (a >= 1) return `$${v.toFixed(2)}`
  if (a === 0) return '$0'
  return `$${v.toPrecision(2)}`
}
function fmtAmt(n) {
  if (n == null || !isFinite(n)) return '—'
  const a = Math.abs(n)
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`
  if (a >= 1) return n.toFixed(2)
  return n.toPrecision(3)
}
const ago = (ms) => {
  if (!ms) return ''
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000))
  return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)}m ago` : `${Math.round(s / 3600)}h ago`
}
const navigateToken = (mint) => window.dispatchEvent(new CustomEvent('solcheddar-navigate-token', { detail: { address: mint } }))

// ───────────────────────── graph ─────────────────────────
const COL = {
  self: '#ffb020', held: '#00cc6a', traded: '#565a63', co: '#c8cfdc', fund: '#4a9eff',
  edge: 'rgba(200,210,230,0.18)', coEdge: 'rgba(255,176,32,0.45)', fundEdge: 'rgba(74,158,255,0.7)',
  label: '#8b8f98', labelHi: '#e6e8ec',
}

function buildGraph(center, holdings, graph, funding, nameOf) {
  const nodes = [{ id: center, kind: 'self', r: 10, label: nameOf(center) || short(center) }]
  const edges = []
  const coins = new Map()
  const held = (holdings?.tokens || []).filter(t => t.valueUsd > 0.5).slice(0, 12)
  held.forEach(t => coins.set(t.mint, { mint: t.mint, value: t.valueUsd, symbol: t.symbol, held: true }))
  ;(graph?.coins || []).slice(0, 12).forEach(c => {
    const prev = coins.get(c.mint)
    const sym = (holdings?.tokens || []).find(t => t.mint === c.mint)?.symbol
    coins.set(c.mint, { ...(prev || { mint: c.mint, value: 0, symbol: sym, held: false }), traded: c })
  })
  const maxV = Math.max(1, ...[...coins.values()].map(c => c.value || 0))
  for (const c of [...coins.values()].slice(0, 20)) {
    nodes.push({ id: 'c:' + c.mint, kind: 'coin', mint: c.mint, held: c.held,
      r: c.held ? 5 + 13 * Math.sqrt((c.value || 0) / maxV) : 4,
      label: c.symbol || short(c.mint), info: c })
    edges.push({ a: center, b: 'c:' + c.mint, kind: c.held ? 'held' : 'traded', w: c.held ? 1.4 : 0.8 })
  }
  const links = graph?.links || []
  const maxS = Math.max(0.01, ...links.map(l => l.score))
  const ids = new Set(nodes.map(n => n.id))
  for (const l of links) {
    nodes.push({ id: l.addr, kind: 'co', r: 4 + 3 * (l.score / maxS), label: nameOf(l.addr) || short(l.addr), info: l })
    ids.add(l.addr)
    edges.push({ a: center, b: l.addr, kind: 'co', w: 0.6 + 3.4 * (l.score / maxS) })
    for (const m of l.mints || []) if (ids.has('c:' + m)) edges.push({ a: l.addr, b: 'c:' + m, kind: 'cocoin', w: 0.5 })
  }
  const cps = funding?.counterparties || []
  const maxF = Math.max(0.001, ...cps.map(c => c.solIn + c.solOut))
  for (const c of cps) {
    if (!ids.has(c.addr)) {
      nodes.push({ id: c.addr, kind: 'fund', r: 4 + 2 * Math.sqrt((c.solIn + c.solOut) / maxF), label: nameOf(c.addr) || short(c.addr), info: c })
      ids.add(c.addr)
    }
    edges.push({ a: center, b: c.addr, kind: 'fund', w: 0.8 + 2.2 * Math.sqrt((c.solIn + c.solOut) / maxF), info: c })
  }
  return { nodes, edges }
}

// Depth: each node gets a z on a flattened sphere around the 2D layout, so
// the front view is today's map. Drag / swipe rotates (yaw + pitch) with
// inertia; double-click on empty space eases back to the front view. The
// layout, the inertia and the ease share ONE rAF loop that stops when idle.
const ROT_K = 0.01          // radians per dragged px
const DRAG_PX = 4           // movement before a press becomes a drag (no click)
const FLATTEN = 0.35        // sphere depth vs radius at rest
const PITCH_MAX = 1.3

function hash01(s) {        // stable per-node pseudo-random in [0,1)
  let h = 2166136261
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) }
  return ((h >>> 0) % 10007) / 10007
}

let shadowSprite = null     // soft round shadow, rendered once
function getShadowSprite() {
  if (shadowSprite) return shadowSprite
  const c = document.createElement('canvas')
  c.width = c.height = 64
  const g = c.getContext('2d')
  const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32)
  grd.addColorStop(0, 'rgba(0,0,0,0.55)')
  grd.addColorStop(0.5, 'rgba(0,0,0,0.25)')
  grd.addColorStop(1, 'rgba(0,0,0,0)')
  g.fillStyle = grd
  g.fillRect(0, 0, 64, 64)
  shadowSprite = c
  return c
}

function haloText(ctx, text, x, y) {
  const fs = ctx.fillStyle
  ctx.lineJoin = 'round'; ctx.lineWidth = 3
  ctx.strokeStyle = 'rgba(0,0,0,0.85)'
  ctx.strokeText(text, x, y)
  ctx.fillStyle = fs
}

// Detector groups (Patterns) get one outline colour each, in both modes.
const GROUP_COLORS = ['#ff5ec4', '#38e1d6', '#c58bff', '#ffd23f', '#ff7a45', '#7dff6a']
const FLAT_KEY = 'solcheddar_walletmap_flat'
const VIEWS_KEY = 'solcheddar:walletmapViews'   // walletmap-dual-20261019: { d2, d3 }
const ZOOM_MIN = 0.3, ZOOM_MAX = 6

function WalletGraph({ center, holdings, graph, funding, nameOf, watchedSet, onCoin, onWallet, transparent = false, fixedW = 0, fixedH = 0,
  flat = false, groupOf = null }) {
  // flat: plain 2D layout (no depth / rotation / perspective / spin);
  // drag pans, wheel zooms at the cursor, double-click empty space resets.
  const flatRef = useRef(flat)
  flatRef.current = flat
  const groupRef = useRef(groupOf)
  groupRef.current = groupOf
  const transparentRef = useRef(transparent)
  transparentRef.current = transparent
  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  const pos = useRef(new Map())      // id -> {x,y,vx,vy}; survives data refreshes
  const sim = useRef({ alpha: 1, raf: 0, hover: null, scale: 1, yaw: 0, pitch: 0, vyaw: 0, vpitch: 0,
    ease: null, drag: null, proj: [], panX: 0, panY: 0, zoom: 1 })
  // fixedW/fixedH (holdings popover): the size is known up front, so the
  // canvas is allocated once and never resized while the layout settles.
  const [size, setSize] = useState(() => fixedW && fixedH ? { w: fixedW, h: fixedH } : { w: 300, h: 260 })
  useEffect(() => {
    if (fixedW && fixedH) setSize(s => (s.w === fixedW && s.h === fixedH ? s : { w: fixedW, h: fixedH }))
  }, [fixedW, fixedH])
  const sizeRef = useRef(size)
  sizeRef.current = size
  const watchedRef = useRef(watchedSet)
  watchedRef.current = watchedSet
  const model = useMemo(() => buildGraph(center, holdings, graph, funding, nameOf),
    [center, holdings, graph, funding, nameOf])
  const modelRef = useRef(model)
  modelRef.current = model
  // True while a fresh layout pre-settles off-screen (only if it spills past
  // the first synchronous chunk): shows 'loading', never stretched frames.
  const [settling, setSettling] = useState(false)

  // Restore this wallet's saved layout + rotation (no re-settle); save it back
  // when the centre changes or the panel closes.
  useEffect(() => {
    const S = sim.current
    const saved = WCACHE.get(center)?.layout
    pos.current = new Map((saved?.pos || []).map(([id, x, y]) => [id, { x, y, vx: 0, vy: 0 }]))
    S.yaw = saved?.yaw || 0; S.pitch = saved?.pitch || 0
    S.vyaw = S.vpitch = 0
    S.ease = null
    S.panX = S.panY = 0; S.zoom = 1
    S.restored = !!saved
    S.snapFit = true                    // show at the right zoom, no visible re-fit
    return () => {
      const P = pos.current
      if (!center || !P.size) return
      cacheEntry(center).layout = {
        pos: [...P.entries()].map(([id, p]) => [id, Math.round(p.x * 10) / 10, Math.round(p.y * 10) / 10]),
        yaw: Math.atan2(Math.sin(S.yaw), Math.cos(S.yaw)), pitch: S.pitch,
      }
      cachePersist()
    }
  }, [center])

  useEffect(() => {
    const el = wrapRef.current
    if (!el || (fixedW && fixedH)) return
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect()
      setSize({ w: Math.max(200, Math.round(r.width)), h: Math.max(180, Math.round(r.height)) })
    })
    ro.observe(el)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!(fixedW && fixedH)])

  useEffect(() => () => {
    const S = sim.current
    cancelAnimationFrame(S.raf); S.raf = 0
    cancelAnimationFrame(S.settleRaf || 0); S.settleRaf = 0
  }, [])

  function stepLayout(S) {
    const { nodes: ns, edges } = modelRef.current
    const P = pos.current
    const pts = ns.map(n => P.get(n.id))
    for (let i = 0; i < ns.length; i++) {
      for (let j = i + 1; j < ns.length; j++) {
        const p = pts[i], q = pts[j]
        if (!p || !q) continue
        let dx = q.x - p.x, dy = q.y - p.y
        let d2 = dx * dx + dy * dy
        if (d2 < 1) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; d2 = 1 }
        const f = (2200 / d2) * S.alpha
        const d = Math.sqrt(d2)
        const fx = (dx / d) * f, fy = (dy / d) * f
        p.vx -= fx; p.vy -= fy; q.vx += fx; q.vy += fy
      }
    }
    for (const e of edges) {
      const p = P.get(e.a), q = P.get(e.b)
      if (!p || !q) continue
      const L = e.kind === 'cocoin' ? 110 : e.kind === 'co' ? 130 : e.kind === 'fund' ? 150 : 80
      const k = e.kind === 'cocoin' ? 0.004 : 0.03
      const dx = q.x - p.x, dy = q.y - p.y
      const d = Math.max(1, Math.hypot(dx, dy))
      const f = (d - L) * k * S.alpha
      const fx = (dx / d) * f, fy = (dy / d) * f
      p.vx += fx; p.vy += fy; q.vx -= fx; q.vy -= fy
    }
    for (let i = 0; i < ns.length; i++) {
      const p = pts[i]
      if (!p) continue
      if (ns[i].kind === 'self') { p.x = 0; p.y = 0; p.vx = 0; p.vy = 0; continue }
      p.vx -= p.x * 0.004 * S.alpha; p.vy -= p.y * 0.004 * S.alpha
      p.vx *= 0.82; p.vy *= 0.82
      p.x += p.vx; p.y += p.vy
    }
    S.alpha *= 0.985
  }

  // One loop for layout, inertia and the reset ease; idle when none is active.
  function frame() {
    const S = sim.current
    S.raf = 0
    let busy = false
    if (S.alpha > 0.03) { stepLayout(S); busy = true; if (S.alpha <= 0.03) S.restored = true }
    if (S.ease) {
      const t = Math.min(1, (performance.now() - S.ease.t0) / S.ease.dur)
      const k = 1 - Math.pow(1 - t, 3)
      S.yaw = S.ease.yaw * (1 - k); S.pitch = S.ease.pitch * (1 - k)
      if (t >= 1) S.ease = null; else busy = true
    } else if (!S.drag && (Math.abs(S.vyaw) > 0.0003 || Math.abs(S.vpitch) > 0.0003)) {
      S.yaw += S.vyaw
      S.pitch = Math.max(-PITCH_MAX, Math.min(PITCH_MAX, S.pitch + S.vpitch))
      S.vyaw *= 0.95; S.vpitch *= 0.95
      busy = true
    }
    draw()
    if (busy) S.raf = requestAnimationFrame(frame)
  }
  function kick() {
    if (!sim.current.raf) sim.current.raf = requestAnimationFrame(frame)
  }

  // Seed new nodes near their anchor, then run the layout until it settles.
  useEffect(() => {
    const P = pos.current
    const edgeOf = new Map(model.edges.map(e => [e.b, e.a]))
    let seeded = 0
    for (const n of model.nodes) {
      if (P.has(n.id)) continue
      seeded++
      if (n.kind === 'self') { P.set(n.id, { x: 0, y: 0, vx: 0, vy: 0 }); continue }
      const anchor = P.get(edgeOf.get(n.id)) || { x: 0, y: 0 }
      const a = Math.random() * Math.PI * 2
      P.set(n.id, { x: anchor.x + Math.cos(a) * 60, y: anchor.y + Math.sin(a) * 60, vx: 0, vy: 0 })
    }
    for (const id of [...P.keys()]) if (!model.nodes.some(n => n.id === id)) P.delete(id)
    // A restored layout with nothing new needs no settling; a few new nodes
    // get a gentle animated one. A fresh map (no cached layout, or mostly new
    // nodes) PRE-SETTLES off-screen: the force sim runs without drawing, in
    // ~8 ms chunks, until it settles or hits 300 ticks / ~120 ms. Then the fit
    // zoom snaps once and the map appears already settled and centred.
    const S = sim.current
    cancelAnimationFrame(S.settleRaf || 0); S.settleRaf = 0
    const fresh = model.nodes.length > 1 && (!S.restored || seeded > Math.max(3, model.nodes.length / 3))
    if (!fresh) {
      S.presettle = false
      setSettling(false)
      S.alpha = !S.restored ? 1 : seeded === 0 ? 0 : 0.4
      kick()
      return
    }
    cancelAnimationFrame(S.raf); S.raf = 0
    S.presettle = true
    S.alpha = S.restored ? 0.6 : 1
    let ticks = 0
    const t0 = performance.now()
    const chunk = () => {
      S.settleRaf = 0
      const c0 = performance.now()
      while (S.alpha > 0.03 && ticks < 300 && performance.now() - c0 < 8) { stepLayout(S); ticks++ }
      if (S.alpha > 0.03 && ticks < 300 && performance.now() - t0 < 120) {
        setSettling(true)
        S.settleRaf = requestAnimationFrame(chunk)
        return
      }
      for (const p of pos.current.values()) { p.vx = 0; p.vy = 0 }
      S.alpha = 0
      S.restored = true
      S.presettle = false
      S.snapFit = true
      setSettling(false)
      kick()                            // one draw, already settled
    }
    chunk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  // Redraw on resize / watched / transparency changes only. (Hover redraws
  // itself in setHover; layout and rotation redraw from the rAF loop.) This
  // used to run after EVERY render, so each parent re-render added a full
  // extra frame — and in the holdings popover that parent re-renders a lot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { sim.current.snapFit = true; draw() }, [size])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { draw() }, [watchedSet, transparent, groupOf])
  // Switching mode: stop any spin, reset pan/zoom, redraw.
  useEffect(() => {
    const S = sim.current
    S.vyaw = S.vpitch = 0; S.ease = null
    S.panX = S.panY = 0; S.zoom = 1
    draw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat])
  // Wheel zoom (flat only). Native non-passive listener so the panel doesn't scroll.
  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    const onWheel = (e) => {
      if (!flatRef.current) return
      e.preventDefault()
      const S = sim.current
      const { w, h } = sizeRef.current
      const rect = cv.getBoundingClientRect()
      const mx = e.clientX - rect.left - w / 2, my = e.clientY - rect.top - h / 2
      const z0 = S.zoom
      const z1 = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z0 * Math.exp(-e.deltaY * 0.0015)))
      // keep the point under the cursor fixed
      S.panX = mx - (mx - S.panX) * (z1 / z0)
      S.panY = my - (my - S.panY) * (z1 / z0)
      S.zoom = z1
      draw()
    }
    cv.addEventListener('wheel', onWheel, { passive: false })
    return () => cv.removeEventListener('wheel', onWheel)
  }, [])

  function draw() {
    const cv = canvasRef.current
    if (!cv || sim.current.presettle) return
    const dpr = window.devicePixelRatio || 1
    const { w, h } = sizeRef.current
    const watchedSet = watchedRef.current
    const { nodes, edges } = modelRef.current
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
      cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr)
    }
    const ctx = cv.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    const P = pos.current
    const S = sim.current
    let R = 60
    for (const n of nodes) { const p = P.get(n.id); if (p) R = Math.max(R, Math.hypot(p.x, p.y) + 20) }
    const D = R * 3.2                                  // camera distance
    const isFlat = flatRef.current
    const cy = Math.cos(S.yaw), sy = Math.sin(S.yaw), cp = Math.cos(S.pitch), sp = Math.sin(S.pitch)

    // Project every node. Position uses perspective RELATIVE to rest, so the
    // un-rotated view is exactly the flat layout; size and dimming use the
    // absolute depth, so there is a subtle depth cue even at rest.
    const proj = []
    const byId = new Map()
    for (const n of nodes) {
      const p = P.get(n.id)
      if (!p) continue
      if (isFlat) {
        const q = { n, ux: p.x, uy: p.y, z: 0, f: 1, a: 1, d: 0 }
        proj.push(q); byId.set(n.id, q)
        continue
      }
      let z = 0
      if (n.kind !== 'self') {
        const u = hash01(n.id)
        const shell = Math.sqrt(Math.max(0, R * R - p.x * p.x - p.y * p.y))
        z = (u < 0.5 ? -1 : 1) * FLATTEN * shell * (0.55 + 0.9 * Math.abs(u - 0.5))
      }
      const x1 = p.x * cy + z * sy
      const z1 = -p.x * sy + z * cy
      const y2 = p.y * cp + z1 * sp
      const z2 = -p.y * sp + z1 * cp
      const f = D / (D - z2), f0 = D / (D - z)
      const d = Math.max(-1, Math.min(1, z2 / R))       // +1 front … -1 back
      const q = { n, ux: x1 * (f / f0), uy: y2 * (f / f0), z: z2, f, a: 0.4 + 0.6 * (d + 1) / 2, d }
      proj.push(q); byId.set(n.id, q)
    }
    // Fit on the PROJECTED layout, so a rotated map stays inside the canvas
    // (identical to the old flat fit when un-rotated).
    let ext = 60
    for (const q of proj) ext = Math.max(ext, Math.abs(q.ux) + q.n.r + 24, Math.abs(q.uy) * (w / h) + q.n.r + 24)
    const target = Math.min(1.4, (w / 2) / ext)
    if (S.snapFit) { S.scale = target; S.snapFit = false }
    else S.scale = S.scale + (target - S.scale) * 0.25
    const sc = isFlat ? S.scale * S.zoom : S.scale
    const ox = isFlat ? S.panX : 0, oy = isFlat ? S.panY : 0
    for (const q of proj) {
      q.x = w / 2 + ox + q.ux * sc; q.y = h / 2 + oy + q.uy * sc
      q.r = Math.max(2.5, q.n.r * Math.min(1, sc + 0.2) * q.f)
    }
    proj.sort((a, b) => a.z - b.z)                     // back to front
    S.proj = proj

    // 1. Shadows (sprite, down-right, bigger offset for nearer nodes).
    const spr = getShadowSprite()
    if (!isFlat) for (const q of proj) {
      const off = (2 + 4 * (q.d + 1) / 2) * q.f
      const s = q.r * 3.2
      ctx.globalAlpha = 0.35 + 0.5 * (q.d + 1) / 2
      ctx.drawImage(spr, q.x + off - s / 2, q.y + off * 1.2 - s / 2, s, s)
    }
    // 2. Links, dimmed and thinned by their mean depth.
    for (const e of edges) {
      const a = byId.get(e.a), b = byId.get(e.b)
      if (!a || !b) continue
      ctx.globalAlpha = (a.a + b.a) / 2
      ctx.beginPath()
      ctx.setLineDash(e.kind === 'fund' ? [4, 4] : [])
      ctx.strokeStyle = e.kind === 'co' ? COL.coEdge : e.kind === 'fund' ? COL.fundEdge
        : e.kind === 'cocoin' ? 'rgba(200,210,230,0.08)' : COL.edge
      ctx.lineWidth = e.w * (a.f + b.f) / 2
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
    }
    ctx.setLineDash([])
    // 3. Nodes + labels, back to front.
    ctx.font = '10px "JetBrains Mono", monospace'
    ctx.textAlign = 'center'
    for (const q of proj) {
      const { n, x, y, r } = q
      const hot = S.hover === n.id
      const base = hot || n.kind === 'self' ? 1 : q.a
      ctx.globalAlpha = base
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2)
      if (n.kind === 'coin' && !n.held) { ctx.strokeStyle = COL.traded; ctx.lineWidth = 1.2; ctx.stroke() }
      else {
        ctx.fillStyle = n.kind === 'self' ? COL.self : n.kind === 'coin' ? COL.held : n.kind === 'fund' ? COL.fund : COL.co
        ctx.globalAlpha = base * (hot || n.kind === 'self' ? 1 : 0.85)
        ctx.fill(); ctx.globalAlpha = base
      }
      if (n.kind !== 'coin' && watchedSet.has(n.id) && n.kind !== 'self') {
        ctx.beginPath(); ctx.arc(x, y, r + 2.5, 0, Math.PI * 2); ctx.strokeStyle = COL.self; ctx.lineWidth = 1.2; ctx.stroke()
      }
      const gc = groupRef.current?.get(n.id)
      if (gc) { ctx.beginPath(); ctx.arc(x, y, r + 1.5, 0, Math.PI * 2); ctx.strokeStyle = gc; ctx.lineWidth = 2; ctx.stroke() }
      if (hot) { ctx.beginPath(); ctx.arc(x, y, r + 4, 0, Math.PI * 2); ctx.strokeStyle = '#fff'; ctx.lineWidth = 1; ctx.stroke() }
      if (n.kind === 'self' || n.kind === 'coin' || hot || nodes.length < 24) {
        ctx.fillStyle = hot || n.kind === 'self' ? COL.labelHi : COL.label
        // On a transparent background (holdings popover) text sits over the
        // chart: dark stroked halo underneath (much cheaper than shadowBlur).
        if (transparentRef.current) haloText(ctx, n.label, x, y - r - 4)
        ctx.fillText(n.label, x, y - r - 4)
      }
    }
    ctx.globalAlpha = 1
    const hn = S.hover && nodes.find(n => n.id === S.hover)
    if (hn) {
      ctx.textAlign = 'left'; ctx.fillStyle = COL.labelHi
      if (transparentRef.current) haloText(ctx, tipOf(hn), 8, h - 8)
      ctx.fillText(tipOf(hn), 8, h - 8)
    }
  }

  function tipOf(n) {
    if (n.kind === 'coin') {
      const c = n.info, t = c.traded
      return `${n.label}${c.held ? ` · held ${fmtUsd(c.value)}` : ''}${t ? ` · ${t.buys}b/${t.sells}s ${t.sol.toFixed(2)}◎ in 72h` : ''} · click to chart`
    }
    if (n.kind === 'co') {
      const l = n.info
      return `${n.label} · ${l.sharedCoins} coin${l.sharedCoins === 1 ? '' : 's'}, ${l.sharedTrades} trades ±60s, ${Math.round(l.sameDir * 100)}% same side · click to recentre`
    }
    if (n.kind === 'fund') {
      const c = n.info
      return `${n.label} · in ${c.solIn.toFixed(3)}◎ out ${c.solOut.toFixed(3)}◎ · ${c.txs} tx · click to recentre`
    }
    return n.label
  }

  // Hit-test on the PROJECTED positions, front-most first.
  const pick = (ev, slop) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const x = ev.clientX - rect.left, y = ev.clientY - rect.top
    const proj = sim.current.proj
    for (let i = proj.length - 1; i >= 0; i--) {
      const q = proj[i]
      if (Math.hypot(q.x - x, q.y - y) < q.r + slop) return q.n
    }
    return null
  }

  const setHover = (id, el, n) => {
    if (id === sim.current.hover) return
    sim.current.hover = id
    if (el) el.style.cursor = n && n.kind !== 'self' ? 'pointer' : 'grab'
    draw()
  }

  return (
    <div ref={wrapRef} className="wmap-graph">
      <canvas
        ref={canvasRef}
        // touch-action none: a swipe on the map rotates it instead of
        // panning the page. Wheel is not handled, so it behaves as before.
        style={{ width: size.w, height: size.h, display: 'block', cursor: 'grab', touchAction: 'none' }}
        onPointerDown={(e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return
          const S = sim.current
          S.drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, lx: e.clientX, ly: e.clientY, lt: performance.now(), moved: false }
          S.vyaw = S.vpitch = 0
          S.ease = null
          try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
        }}
        onPointerMove={(e) => {
          const S = sim.current
          const g = S.drag
          if (g && g.id === e.pointerId) {
            if (!g.moved && Math.hypot(e.clientX - g.x0, e.clientY - g.y0) > DRAG_PX) {
              g.moved = true
              e.currentTarget.style.cursor = 'grabbing'
              if (S.hover) { S.hover = null }
            }
            if (g.moved && flatRef.current) {
              S.panX += e.clientX - g.lx; S.panY += e.clientY - g.ly
              g.lx = e.clientX; g.ly = e.clientY
              draw()
            } else if (g.moved) {
              const now = performance.now()
              const dt = Math.max(1, now - g.lt)
              const dyaw = (e.clientX - g.lx) * ROT_K, dpitch = (e.clientY - g.ly) * ROT_K
              S.yaw += dyaw
              S.pitch = Math.max(-PITCH_MAX, Math.min(PITCH_MAX, S.pitch + dpitch))
              // velocity in radians per ~16 ms frame, smoothed, for the flick
              S.vyaw = S.vyaw * 0.5 + (dyaw * 16 / dt) * 0.5
              S.vpitch = S.vpitch * 0.5 + (dpitch * 16 / dt) * 0.5
              g.lx = e.clientX; g.ly = e.clientY; g.lt = now
              kick()
            }
            return
          }
          if (e.pointerType !== 'mouse') return
          const n = pick(e, 6)
          setHover(n ? n.id : null, e.currentTarget, n)
        }}
        onPointerUp={(e) => {
          const S = sim.current
          const g = S.drag
          if (!g || g.id !== e.pointerId) return
          S.drag = null
          e.currentTarget.style.cursor = 'grab'
          if (g.moved) {
            if (performance.now() - g.lt > 80) { S.vyaw = S.vpitch = 0 }   // held still before release
            kick()
            return
          }
          // A press that did not move is a click.
          const touch = e.pointerType !== 'mouse'
          const n = pick(e, touch ? 14 : 6)
          if (!n || n.kind === 'self') return
          if (touch && S.hover !== n.id) { S.hover = n.id; draw(); return }  // first tap = inspect
          if (n.kind === 'coin') onCoin(n.mint)
          else onWallet(n.id)
        }}
        onPointerCancel={() => { sim.current.drag = null }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse' && !sim.current.drag) setHover(null, e.currentTarget, null) }}
        onDoubleClick={(e) => {
          if (pick(e, 6)) return
          const S = sim.current
          if (flatRef.current) { S.panX = S.panY = 0; S.zoom = 1; draw(); return }
          const yaw = Math.atan2(Math.sin(S.yaw), Math.cos(S.yaw))   // shortest way home
          S.yaw = yaw
          S.vyaw = S.vpitch = 0
          S.ease = { yaw, pitch: S.pitch, t0: performance.now(), dur: 450 }
          kick()
        }}
      />
      {settling && (
        <div className="wmap-canvas-status">
          <span className="wmap-loading">loading<span className="wmap-dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span></span>
        </div>
      )}
    </div>
  )
}

// ───────────────────────── flat 2D: Timeline / Clock ─────────────────────────
// Rows = wallets (this wallet, then Patterns groups kept together, then top
// co-traders). Timeline: time on X, one dot per trade (green buy / red sell,
// radius √SOL); a faint guide where 3+ wallets trade within 2 s. Clock: 24
// hour columns (Central time) per wallet, shade = trades that hour, buys
// on the top half, sells on the bottom half — routines show as stripes.
const RT_ROW = 14, RT_LABEL = 86, RT_AXIS = 18, RT_MAXW = 60
const CT_TZ = 'America/Chicago'
const ctHourFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23' })
const ctTimeFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
const ctShortFmt = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
const ctHour = (ms) => Number(ctHourFmt.format(ms)) % 24

function RoutinesView({ center, graph, holdings, pat, nameOf, onWallet, onLoadPatterns, patLoading, currentMint = '', highlight = null }) {
  // highlight: { addrs, mint, t0, t1, afterH } from the chart's Absorbers panel
  const hiSet = useMemo(() => new Set(highlight?.addrs || []), [highlight])
  // Coin filter: default = the coin on the chart (else this wallet's top coin).
  // '' = All coins (server per-wallet index; may answer 'index building').
  const coinOpts = useMemo(() => {
    const out = [], seen = new Set()
    const sym = new Map((holdings?.tokens || []).map(t => [t.mint, t.symbol]))
    const add = (m, tag) => { if (m && isWalletAddress(m) && !seen.has(m)) { seen.add(m); out.push({ m, label: `${sym.get(m) || short(m)}${tag || ''}` }) } }
    add(currentMint, ' (chart)')
    add(highlight?.mint, ' (absorb)')
    ;(graph?.coins || []).forEach(c => add(c.mint))
    return out
  }, [currentMint, graph, holdings, highlight])
  const [coin, setCoin] = useState(null)        // null = default
  useEffect(() => { setCoin(null) }, [center])
  const mint = coin === null ? (highlight?.mint || coinOpts[0]?.m || '') : coin
  const [tab, setTab] = useState('timeline')
  const [hours, setHours] = useState(24)
  useEffect(() => {
    if (!highlight?.t0) return
    setCoin(null); setTab('timeline')
    const need = (Date.now() - highlight.t0) / 3_600_000 + 1
    setHours(need > 24 ? 72 : 24)
  }, [highlight])
  const [act, setAct] = useState({ data: null, err: null, loading: false })
  const wrapRef = useRef(null)
  const canvasRef = useRef(null)
  const [width, setWidth] = useState(300)
  const view = useRef({ t0: 0, t1: 0, hover: null, drag: null, hits: [] })

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setWidth(Math.max(240, Math.round(el.getBoundingClientRect().width))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Row order: me, each Patterns group together (colour strip), then co-traders.
  const rows = useMemo(() => {
    const out = [], seen = new Set()
    const add = (addr, color, group) => {
      if (!addr || seen.has(addr) || out.length >= RT_MAXW) return
      seen.add(addr); out.push({ addr, color, group })
    }
    add(center, COL.self, -1)
    ;(highlight?.addrs || []).forEach(a => add(a, '#ffd23f', -3))
    ;(pat?.clusters || []).forEach((c, i) => c.wallets.forEach(w => add(w, GROUP_COLORS[i % GROUP_COLORS.length], i)))
    ;[...(graph?.links || [])].sort((a, b) => (b.score || 0) - (a.score || 0)).forEach(l => add(l.addr, null, -2))
    return out
  }, [center, graph, pat, highlight])
  const key = rows.map(r => r.addr).sort().join(',')

  useEffect(() => {
    if (!rows.length) return
    let alive = true
    setAct({ data: null, loading: true, err: null })
    fetchWalletActivity(rows.map(r => r.addr), hours, mint)
      .then(d => { if (alive) setAct({ data: d, err: null, loading: false }) })
      .catch(e => { if (alive) setAct(s => ({ data: s.data, err: e.message, loading: false })) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, hours, mint])

  const symOf = useMemo(() => {
    const m = new Map((holdings?.tokens || []).map(t => [t.mint, t.symbol]))
    return (mint) => m.get(mint) || short(mint)
  }, [holdings])

  // Derived: extent, same-moment guides, clock counts.
  const derived = useMemo(() => {
    const d = act.data
    if (!d) return null
    let lo = Infinity, hi = -Infinity, maxSol = 0.01
    const all = []
    rows.forEach((r, ri) => {
      for (const t of d.wallets?.[r.addr] || []) {
        lo = Math.min(lo, t[0]); hi = Math.max(hi, t[0]); maxSol = Math.max(maxSol, t[2])
        all.push([t[0], ri])
      }
    })
    if (!all.length) return { empty: true }
    all.sort((a, b) => a[0] - b[0])
    const guides = []
    for (let i = 0, j = 0; i < all.length; i++) {
      while (all[i][0] - all[j][0] > 2000) j++
      const ws = new Set(); for (let k = j; k <= i; k++) ws.add(all[k][1])
      if (ws.size >= 3) {
        const t = (all[j][0] + all[i][0]) / 2
        if (!guides.length || t - guides[guides.length - 1] > 2000) guides.push(t)
        else guides[guides.length - 1] = t
      }
    }
    const clock = rows.map(r => {
      const b = new Array(24).fill(0), s = new Array(24).fill(0)
      for (const t of d.wallets?.[r.addr] || []) (t[1] ? b : s)[ctHour(t[0])]++
      return { b, s }
    })
    const maxCell = Math.max(1, ...clock.flatMap(c => c.b.map((v, h) => v + c.s[h])))
    const pad = Math.max(60_000, (hi - lo) * 0.03)
    return { lo: lo - pad, hi: hi + pad, maxSol, guides, clock, maxCell }
  }, [act.data, rows])
  useEffect(() => {
    if (!derived?.lo) return
    view.current.t0 = derived.lo; view.current.t1 = derived.hi
    // Absorbers: open zoomed on the absorb bar through the 'later' window.
    if (highlight?.t0 && highlight.t0 < derived.hi && highlight.t1 > derived.lo - 3_600_000) {
      const a = Math.max(derived.lo, highlight.t0 - 30 * 60_000)
      const b = Math.min(derived.hi, Math.max(highlight.t1 + (highlight.afterH || 6) * 3_600_000, a + 60_000))
      if (b > a) { view.current.t0 = a; view.current.t1 = b }
    }
  }, [derived, highlight])

  const height = RT_AXIS + rows.length * RT_ROW + 4
  const ref = useRef({}); ref.current = { rows, derived, act, width, height, tab, hiSet, highlight }

  function draw() {
    const cv = canvasRef.current
    if (!cv) return
    const { rows, derived, act, width: w, height: h, tab, hiSet, highlight } = ref.current
    const dpr = window.devicePixelRatio || 1
    if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr) }
    const ctx = cv.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    ctx.font = '10px "JetBrains Mono", monospace'
    const V = view.current
    const hits = []
    const X0 = RT_LABEL, X1 = w - 6
    // row labels
    rows.forEach((r, i) => {
      const y = RT_AXIS + i * RT_ROW
      if (i % 2) { ctx.fillStyle = 'rgba(255,255,255,0.02)'; ctx.fillRect(0, y, w, RT_ROW) }
      if (hiSet.has(r.addr)) { ctx.fillStyle = 'rgba(255,210,63,0.06)'; ctx.fillRect(0, y, w, RT_ROW) }
      if (r.color) { ctx.fillStyle = r.color; ctx.fillRect(0, y + 2, 3, RT_ROW - 4) }
      const hot = V.hover?.row === i && V.hover.label
      ctx.fillStyle = r.addr === center ? COL.self : hot ? COL.labelHi : hiSet.has(r.addr) ? '#ffd23f' : '#8b8f98'
      ctx.textAlign = 'left'
      ctx.fillText((nameOf(r.addr) || short(r.addr)).slice(0, 12), 7, y + 10)
      hits.push({ kind: 'label', row: i, x0: 0, x1: X0 - 4, y0: y, y1: y + RT_ROW })
      if (i && rows[i - 1].group !== r.group) { ctx.fillStyle = 'rgba(255,255,255,0.08)'; ctx.fillRect(0, y, w, 1) }
    })
    ctx.fillStyle = '#3a3d44'; ctx.fillRect(X0 - 2, RT_AXIS - 2, 1, h - RT_AXIS)
    if (!derived || derived.empty) {
      ctx.fillStyle = '#666'; ctx.textAlign = 'center'
      ctx.fillText(act.loading ? 'loading trades…' : act.err ? (/index building/i.test(act.err) ? 'index building…' : 'trades unavailable (see above)') : derived?.empty ? `no trades in ${hours}h` : '', (X0 + X1) / 2, RT_AXIS + 12)
      V.hits = hits; return
    }
    if (tab === 'timeline') {
      const t0 = V.t0, t1 = V.t1, xs = (t) => X0 + (t - t0) / (t1 - t0) * (X1 - X0)
      // ticks
      const span = t1 - t0, raw = span / Math.max(2, (X1 - X0) / 80)
      const steps = [1e3, 5e3, 15e3, 60e3, 300e3, 900e3, 1800e3, 3600e3, 3 * 3600e3, 6 * 3600e3, 12 * 3600e3]
      const step = steps.find(s => s >= raw) || 86400e3
      ctx.textAlign = 'center'
      for (let t = Math.ceil(t0 / step) * step; t <= t1; t += step) {
        const x = Math.round(xs(t)) + 0.5
        ctx.fillStyle = 'rgba(255,255,255,0.04)'; ctx.fillRect(x, RT_AXIS, 1, h - RT_AXIS)
        ctx.fillStyle = '#666'
        const lab = step < 60e3 ? ctTimeFmt.format(t).split(', ').pop() : ctShortFmt.format(t)
        ctx.fillText(lab, x, 11)
      }
      ctx.save(); ctx.beginPath(); ctx.rect(X0, 0, X1 - X0 + 6, h); ctx.clip()
      ctx.fillStyle = 'rgba(255,210,63,0.10)'
      for (const g of derived.guides) { const x = xs(g); if (x >= X0 && x <= X1) ctx.fillRect(x - 1, RT_AXIS, 2, h - RT_AXIS) }
      if (highlight?.t0) {
        // absorb bar (gold) and the 'later' window after it (faint)
        const a = xs(highlight.t0), b = Math.max(a + 2, xs(highlight.t1))
        const c = xs(highlight.t1 + (highlight.afterH || 6) * 3_600_000)
        ctx.fillStyle = 'rgba(255,210,63,0.035)'; ctx.fillRect(b, RT_AXIS, Math.max(0, c - b), h - RT_AXIS)
        ctx.fillStyle = 'rgba(255,210,63,0.22)'; ctx.fillRect(a, RT_AXIS, b - a, h - RT_AXIS)
      }
      rows.forEach((r, i) => {
        const cy = RT_AXIS + i * RT_ROW + RT_ROW / 2
        for (const t of act.data.wallets?.[r.addr] || []) {
          const x = xs(t[0])
          if (x < X0 - 6 || x > X1 + 6) continue
          const rad = 1.5 + 4.5 * Math.sqrt(t[2] / derived.maxSol)
          const hot = V.hover?.trade === t
          ctx.globalAlpha = hot ? 1 : (hiSet.size && !hiSet.has(r.addr) && r.addr !== center) ? 0.25 : 0.8
          ctx.fillStyle = t[1] ? '#00cc6a' : '#ff4d5e'
          ctx.beginPath(); ctx.arc(x, cy, rad, 0, Math.PI * 2); ctx.fill()
          if (hot) { ctx.globalAlpha = 1; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1; ctx.stroke() }
          hits.push({ kind: 'trade', row: i, x, y: cy, r: rad + 2, trade: t })
        }
      })
      ctx.globalAlpha = 1; ctx.restore()
    } else {
      const cw = (X1 - X0) / 24
      ctx.textAlign = 'center'; ctx.fillStyle = '#666'
      for (let hh = 0; hh < 24; hh += (cw < 14 ? 3 : 1)) ctx.fillText(String(hh), X0 + (hh + 0.5) * cw, 11)
      rows.forEach((r, i) => {
        const y = RT_AXIS + i * RT_ROW, c = derived.clock[i]
        for (let hh = 0; hh < 24; hh++) {
          const x = X0 + hh * cw
          ctx.fillStyle = 'rgba(255,255,255,0.025)'; ctx.fillRect(x + 0.5, y + 1, cw - 1, RT_ROW - 2)
          const half = (RT_ROW - 2) / 2
          if (c.b[hh]) { ctx.fillStyle = `rgba(0,204,106,${0.2 + 0.8 * Math.sqrt(c.b[hh] / derived.maxCell)})`; ctx.fillRect(x + 0.5, y + 1, cw - 1, half) }
          if (c.s[hh]) { ctx.fillStyle = `rgba(255,77,94,${0.2 + 0.8 * Math.sqrt(c.s[hh] / derived.maxCell)})`; ctx.fillRect(x + 0.5, y + 1 + half, cw - 1, half) }
          if (V.hover?.cell && V.hover.row === i && V.hover.hour === hh) { ctx.strokeStyle = '#fff'; ctx.lineWidth = 1; ctx.strokeRect(x + 0.5, y + 1, cw - 1, RT_ROW - 2) }
          hits.push({ kind: 'cell', row: i, hour: hh, x0: x, x1: x + cw, y0: y, y1: y + RT_ROW })
        }
      })
    }
    V.hits = hits
  }
  const drawRef = useRef(draw); drawRef.current = draw
  useEffect(() => { drawRef.current() })

  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    const onWheel = (e) => {
      const { derived, tab, width: w } = ref.current
      if (tab !== 'timeline' || !derived?.lo) return
      e.preventDefault()
      const V = view.current
      const mx = e.clientX - cv.getBoundingClientRect().left
      const X0 = RT_LABEL, X1 = w - 6
      const tc = V.t0 + (Math.max(X0, Math.min(X1, mx)) - X0) / (X1 - X0) * (V.t1 - V.t0)
      const k = Math.exp(e.deltaY * 0.0015)
      const span = Math.max(2000, Math.min(derived.hi - derived.lo, (V.t1 - V.t0) * k))
      const f = (tc - V.t0) / (V.t1 - V.t0)
      V.t0 = tc - f * span; V.t1 = V.t0 + span
      drawRef.current()
    }
    cv.addEventListener('wheel', onWheel, { passive: false })
    return () => cv.removeEventListener('wheel', onWheel)
  }, [])

  const pick = (e) => {
    const r = canvasRef.current.getBoundingClientRect()
    const x = e.clientX - r.left, y = e.clientY - r.top
    let best = null, bd = 1e9
    for (const h of view.current.hits) {
      if (h.kind === 'trade') { const d = Math.hypot(h.x - x, h.y - y); if (d < h.r && d < bd) { best = h; bd = d } }
      else if (x >= h.x0 && x < h.x1 && y >= h.y0 && y < h.y1 && !best) best = h
    }
    return best
  }
  const hv = view.current.hover
  const [, force] = useState(0)
  let tip = ''
  if (hv?.trade) {
    const t = hv.trade, a = rows[hv.row]?.addr
    tip = `${nameOf(a) || short(a)} · ${t[1] ? 'buy' : 'sell'} ${t[2].toFixed(3)}◎ · ${ctTimeFmt.format(t[0])} CT · ${symOf(act.data.mints[t[3]])}`
  } else if (hv?.cell && derived?.clock) {
    const c = derived.clock[hv.row], a = rows[hv.row]?.addr
    tip = `${nameOf(a) || short(a)} · ${String(hv.hour).padStart(2, '0')}:00–${String(hv.hour).padStart(2, '0')}:59 CT · ${c.b[hv.hour]} buys, ${c.s[hv.hour]} sells (${hours}h)`
  } else if (hv?.label) {
    const a = rows[hv.row]?.addr
    tip = `${a} · ${(act.data?.wallets?.[a] || []).length} trades${a !== center ? ' · click to open' : ''}`
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '2px 4px', fontSize: 10, color: '#666' }}>
        <button className={`wmap-btn${tab === 'timeline' ? ' on' : ''}`} onClick={() => setTab('timeline')} title="Every trade on a time axis; same-moment buys line up">Timeline</button>
        <button className={`wmap-btn${tab === 'clock' ? ' on' : ''}`} onClick={() => setTab('clock')} title="Trades per hour of day (CT): routines">Clock</button>
        <button className="wmap-btn" onClick={() => setHours(h => (h === 24 ? 72 : 24))} title="Window">{hours}h</button>
        <select className="wmap-btn" value={mint} onChange={(e) => setCoin(e.target.value)} title="Coin filter (All coins uses the per-wallet index)"
          style={{ background: '#111', color: '#8b8f98', border: '1px solid #2a2a2a', fontSize: 10, maxWidth: 110 }}>
          {coinOpts.map(o => <option key={o.m} value={o.m}>{o.label}</option>)}
          <option value="">All coins</option>
        </select>
        <span style={{ flex: 1 }} />
        {!pat && <button className="wmap-btn" disabled={patLoading} onClick={onLoadPatterns} title="Group rows by Patterns">{patLoading ? 'Loading…' : 'Group by patterns'}</button>}
        <span>{rows.length} wallets{act.data?.capped ? ' · capped' : ''}{act.loading && act.data ? ' · …' : ''}</span>
      </div>
      {act.err && (/index building/i.test(act.err)
        ? <div className="wmap-dim wmap-pad" style={{ color: '#666' }}>All-coins index is still building{(act.err.match(/\(([\d.]+%)\)/) || [])[1] ? ` (${act.err.match(/\(([\d.]+%)\)/)[1]})` : ''} — try again shortly, or pick a coin.</div>
        : <div className="wmap-err" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>Trades: {act.err}{act.data ? ' (showing last result)' : ''}</div>)}
      <div ref={wrapRef} className="wmap-graph" style={{ height: 'auto', maxHeight: 420, overflowY: 'auto', overflowX: 'hidden' }}>
        <canvas ref={canvasRef}
          style={{ width, height, display: 'block', cursor: 'default', touchAction: 'pan-y' }}
          onPointerDown={(e) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return
            view.current.drag = { id: e.pointerId, x0: e.clientX, lx: e.clientX, moved: false }
          }}
          onPointerMove={(e) => {
            const V = view.current, g = V.drag
            if (g && g.id === e.pointerId && ref.current.tab === 'timeline' && derived?.lo) {
              if (!g.moved && Math.abs(e.clientX - g.x0) > DRAG_PX) { g.moved = true; try { e.currentTarget.setPointerCapture(e.pointerId) } catch {} }
              if (g.moved) {
                const dt = -(e.clientX - g.lx) / (width - 6 - RT_LABEL) * (V.t1 - V.t0)
                V.t0 += dt; V.t1 += dt; g.lx = e.clientX; drawRef.current()
                return
              }
            }
            const h = pick(e)
            const nh = h ? { row: h.row, trade: h.trade, cell: h.kind === 'cell', hour: h.hour, label: h.kind === 'label' } : null
            const same = (nh && V.hover) ? nh.row === V.hover.row && nh.trade === V.hover.trade && nh.hour === V.hover.hour && nh.label === V.hover.label : nh === V.hover
            if (same) return
            V.hover = nh
            e.currentTarget.style.cursor = nh?.label && rows[nh.row]?.addr !== center ? 'pointer' : ref.current.tab === 'timeline' ? 'grab' : 'default'
            force(n => n + 1)
          }}
          onPointerUp={(e) => {
            const g = view.current.drag
            view.current.drag = null
            if (g?.moved) return
            const h = pick(e)
            if (h?.kind === 'label' && rows[h.row]?.addr !== center) onWallet(rows[h.row].addr)
          }}
          onPointerCancel={() => { view.current.drag = null }}
          onPointerLeave={() => { if (!view.current.drag && view.current.hover) { view.current.hover = null; force(n => n + 1) } }}
          onDoubleClick={() => { if (derived?.lo) { view.current.t0 = derived.lo; view.current.t1 = derived.hi; drawRef.current() } }}
        />
      </div>
      <div className="wmap-dim" style={{ minHeight: 14, padding: '2px 4px', fontSize: 10, color: tip ? COL.labelHi : '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {tip || (tab === 'timeline' ? 'green buy · red sell · size √SOL · yellow guide = 3+ wallets within 2s · wheel zoom, drag pan, dbl-click reset'
          : 'hour of day (CT) · top half buys, bottom half sells · darker = more trades')}
      </div>
    </div>
  )
}

// ───────────────────────── reusable map ─────────────────────────
// <WalletMapCanvas address=… transparent /> — the same map (3D rotate,
// shadows, hit-testing) with its own data loading through the SHARED client
// cache and /api/wallet endpoints, so it never costs extra server/Helius
// calls when the panel has already loaded the wallet (and vice versa).
// Mount it with key={address} so a wallet switch starts clean.
const EMPTY_SET = new Set()
const noName = () => ''
export const WalletMapCanvas = memo(function WalletMapCanvas({ address, transparent = false, hours = 72, style, className = '',
  width = 0, height = 0, nameOf = noName, watchedSet = EMPTY_SET, onCoin = navigateToken, onWallet = openWalletMap }) {
  const [hold, setHold] = useState(() => (address && cacheAny(address, 'hold')?.data) || null)
  const [graph, setGraph] = useState(() => (address && cacheAny(address, 'graph')?.data) || null)
  const [status, setStatus] = useState({ loading: false, err: null })
  useEffect(() => {
    if (!address) return
    let alive = true
    cacheEntry(address)
    const ch = cacheAny(address, 'hold'), cg = cacheAny(address, 'graph')
    const jobs = []
    if (!cacheFresh(address, 'hold')) {
      jobs.push(fetchWalletHoldings(address, { force: !!ch }).then(d => { cachePut(address, 'hold', d); if (alive) setHold(d) }))
    }
    if (!cacheFresh(address, 'graph')) {
      jobs.push(fetchWalletGraph(address, hours).then(d => { cachePut(address, 'graph', d); if (alive) setGraph(d) }))
    }
    if (!jobs.length) return () => { alive = false }
    // Loading text only when nothing is cached; otherwise refresh quietly.
    if (!ch && !cg) setStatus({ loading: true, err: null })
    Promise.allSettled(jobs).then(rs => {
      if (!alive) return
      const bad = rs.find(r => r.status === 'rejected')
      setStatus({ loading: false, err: bad && !cacheAny(address, 'graph') ? bad.reason?.message || 'unavailable' : null })
    })
    return () => { alive = false }
  }, [address, hours])
  const fund = (address && cacheFresh(address, 'fund')?.data) || null
  if (!address) return null
  return (
    <div className={`wmap-canvas${transparent ? ' transparent' : ''} ${className}`} style={style}>
      <WalletGraph center={address} holdings={hold} graph={graph} funding={fund} nameOf={nameOf}
        watchedSet={watchedSet} onCoin={onCoin} onWallet={onWallet} transparent={transparent}
        fixedW={width} fixedH={height} />
      {!graph && (status.loading || status.err) && (
        <div className={`wmap-canvas-status${status.err ? ' err' : ''}`}>
          {status.err || <span className="wmap-loading">loading<span className="wmap-dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span></span>}
        </div>
      )}
    </div>
  )
})

// ───────────────────────── panel ─────────────────────────
export default function WalletMapPanel({ watchedWallets = [], setWatchedWallets, showToast, maxWatched = 10, currentMint = '' }) {
  const [stack, setStack] = useState([])
  const addr = stack[stack.length - 1] || null
  const [hold, setHold] = useState({ data: null, err: null, loading: false })
  const [graph, setGraph] = useState({ data: null, err: null, loading: false })
  const [fund, setFund] = useState({ data: null, err: null, loading: false })
  const [pat, setPat] = useState({ data: null, err: null, loading: false })
  // walletmap-dual-20261019: 2D and 3D are independent toggles (never both off).
  const [views, setViews] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(VIEWS_KEY) || 'null')
      if (v && (v.d2 || v.d3)) return { d2: !!v.d2, d3: !!v.d3 }
      const f = localStorage.getItem(FLAT_KEY) === '1'   // migrate old single toggle
      return { d2: f, d3: !f }
    } catch { return { d2: false, d3: true } }
  })
  useEffect(() => { try { localStorage.setItem(VIEWS_KEY, JSON.stringify(views)) } catch {} }, [views])
  const toggleView = (k) => setViews(v => { const n = { ...v, [k]: !v[k] }; return (n.d2 || n.d3) ? n : v })
  const setFlat = (on) => { if (on) setViews(v => (v.d2 ? v : { ...v, d2: true })) }
  const [hilite, setHilite] = useState(null)   // Absorbers -> Timeline highlight
  // wallet -> outline colour, one per Patterns group (first group wins)
  const groupOf = useMemo(() => {
    const cl = pat.data?.clusters
    if (!cl?.length) return null
    const m = new Map()
    cl.forEach((c, i) => c.wallets.forEach(w => { if (!m.has(w)) m.set(w, GROUP_COLORS[i % GROUP_COLORS.length]) }))
    return m
  }, [pat.data])
  const reqRef = useRef(0)
  const openedAt = useRef(0)

  useEffect(() => {
    const onOpen = (e) => {
      const a = e.detail?.address
      if (!isWalletAddress(a)) return
      openedAt.current = Date.now()
      const hl = e.detail?.highlight
      if (Array.isArray(hl) && hl.length) {
        setHilite({ addrs: hl.filter(isWalletAddress).slice(0, 30), mint: e.detail.mint || '', t0: e.detail.t0, t1: e.detail.t1, afterH: e.detail.afterH || 6 })
        setFlat(true)
      } else setHilite(null)
      setStack(prev => (prev[prev.length - 1] === a ? prev : [a]))
    }
    window.addEventListener('solcheddar-open-wallet', onOpen)
    return () => window.removeEventListener('solcheddar-open-wallet', onOpen)
  }, [])

  useEffect(() => {
    if (!addr) return
    const onKey = (e) => { if (e.key === 'Escape') setStack([]) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [addr])

  // quiet = background refresh of data already on screen: no loading state.
  const loadHoldings = useCallback(async (force, quiet = false) => {
    if (!addr) return
    const id = reqRef.current
    if (!quiet) setHold(s => ({ ...s, loading: true }))
    try {
      const d = await fetchWalletHoldings(addr, { force })
      cachePut(addr, 'hold', d)
      if (id === reqRef.current) setHold({ data: d, err: null, loading: false })
    } catch (e) {
      // Keep the last good holdings on a failed refresh — never blank to "0 tokens".
      if (id === reqRef.current) setHold(s => ({ data: s.data, err: e.message, loading: false }))
    }
  }, [addr])

  const loadGraph = useCallback(async (quiet = false) => {
    if (!addr) return
    const id = reqRef.current
    if (!quiet) setGraph(s => ({ ...s, loading: true }))
    try {
      const d = await fetchWalletGraph(addr, 72)
      cachePut(addr, 'graph', d)
      if (id === reqRef.current) setGraph({ data: d, err: null, loading: false })
    } catch (e) {
      if (id === reqRef.current) setGraph(s => ({ data: s.data, err: e.message, loading: false }))
    }
  }, [addr])

  const loadFunding = useCallback(async () => {
    if (!addr) return
    const id = reqRef.current
    setFund({ data: null, err: null, loading: true })
    try {
      const d = await fetchWalletFunding(addr)
      if (id !== reqRef.current) return
      if (d.paused) setFund({ data: null, err: 'paused: daily data budget used up — try tomorrow', loading: false })
      else if (d.unavailable) setFund({ data: null, err: 'funding data not configured', loading: false })
      else { cachePut(addr, 'fund', d); setFund({ data: d, err: null, loading: false }) }
    } catch (e) {
      if (id === reqRef.current) setFund({ data: null, err: e.message, loading: false })
    }
  }, [addr])

  // Patterns: cross-coin groups this wallet buys with (0 credits, 24h).
  const loadPatterns = useCallback(async () => {
    if (!addr) return
    const id = reqRef.current
    setPat({ data: null, err: null, loading: true })
    try {
      const d = await fetchWalletClusters(24, addr)
      if (id === reqRef.current) setPat({ data: d, err: null, loading: false })
    } catch (e) {
      if (id === reqRef.current) setPat({ data: null, err: e.message, loading: false })
    }
  }, [addr])

  // Open / Back: render whatever is cached at once, then refresh only what is
  // stale — quietly when something is already on screen.
  // The cached state is swapped in DURING render (not in an effect), so the
  // graph never renders one frame of the previous wallet's data — that frame
  // would wipe the restored layout.
  const [shownAddr, setShownAddr] = useState(null)
  if (addr !== shownAddr) {
    setShownAddr(addr)
    setHold({ data: (addr && cacheAny(addr, 'hold')?.data) || null, err: null, loading: false })
    setGraph({ data: (addr && cacheAny(addr, 'graph')?.data) || null, err: null, loading: false })
    setFund({ data: (addr && cacheFresh(addr, 'fund')?.data) || null, err: null, loading: false })
    setPat({ data: null, err: null, loading: false })
  }
  useEffect(() => {
    reqRef.current++
    if (!addr) return
    cacheEntry(addr)
    const ch = cacheAny(addr, 'hold'), cg = cacheAny(addr, 'graph')
    if (!cacheFresh(addr, 'hold')) loadHoldings(!!ch, !!ch)
    if (!cacheFresh(addr, 'graph')) loadGraph(!!cg)
  }, [addr, loadHoldings, loadGraph])

  const watched = watchedWallets.find(w => w.address === addr)
  useEffect(() => {
    if (!addr || !watched) return
    const t = setInterval(() => { if (document.visibilityState === 'visible') loadHoldings(true, true) }, REFRESH_MS)
    return () => clearInterval(t)
  }, [addr, watched, loadHoldings])

  const labels = useMemo(() => new Map(watchedWallets.map(w => [w.address, w.label])), [watchedWallets])
  const nameOf = useCallback((a) => labels.get(a) || '', [labels])
  const watchedSet = useMemo(() => new Set(watchedWallets.map(w => w.address)), [watchedWallets])

  const toggleFollow = () => {
    if (!addr || !setWatchedWallets) return
    if (watched) { setWatchedWallets(prev => prev.filter(w => w.address !== addr)); return }
    if (watchedWallets.length >= maxWatched) { showToast?.(`Max ${maxWatched} wallets`, 'sell'); return }
    setWatchedWallets(prev => [...prev, { address: addr, label: '' }])
  }
  const copy = () => { try { navigator.clipboard.writeText(addr); showToast?.('Address copied', 'buy') } catch {} }

  if (!addr) return null
  const h = hold.data
  const g = graph.data
  const budget = (fund.data || h || g)?.budget

  return createPortal(
    // Ignore backdrop clicks right after opening: the 2nd click of a
    // double-click on the opener would otherwise close the panel at once.
    <div className="wmap-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && Date.now() - openedAt.current > 450) setStack([]) }}>
      <div className="wmap-panel" role="dialog" aria-label="Wallet">
        <div className="wmap-rail">
          {stack.length > 1 && <button className="wmap-btn" onClick={() => setStack(s => s.slice(0, -1))} title="Back">‹ Back</button>}
          <span className="wmap-title" title={addr} onClick={copy}>{watched?.label || short(addr)}</span>
          <span className="wmap-addr" title="Copy address" onClick={copy}>{short(addr)}</span>
          <span style={{ flex: 1 }} />
          <a className="wmap-btn" href={`https://solscan.io/account/${addr}`} target="_blank" rel="noreferrer">Solscan</a>
          <button className={`wmap-btn${watched ? ' on' : ''}`} onClick={toggleFollow}>{watched ? 'Following' : '+ Follow'}</button>
          <button className="wmap-x" onClick={() => setStack([])} aria-label="Close">×</button>
        </div>

        <div className="wmap-body">
          <div className="wmap-section-head">
            <span>Holdings</span>
            <span className="wmap-dim">
              {h ? `${fmtUsd(h.totalUsd)} · ${h.tokenCount} token${h.tokenCount === 1 ? '' : 's'} · ${ago(h.at)}` : ''}
              {watched ? ' · auto 5m' : ''}
            </span>
            <span style={{ flex: 1 }} />
            <button className="wmap-btn" disabled={hold.loading} onClick={() => loadHoldings(true)}>{hold.loading ? '…' : '↻'}</button>
          </div>
          {hold.err && <div className="wmap-err">{hold.err}{h ? ' (showing last result)' : ''}</div>}
          {!h && hold.loading && <div className="wmap-dim wmap-pad">Loading holdings…</div>}
          {h && (
            <div className="wmap-holdings">
              <div className="wmap-hrow head"><span>Token</span><span>Amount</span><span>Value</span><span>MCap</span></div>
              <div className="wmap-hrow sol">
                <span>SOL</span><span>{fmtAmt(h.sol.amount)}</span><span>{fmtUsd(h.sol.usd)}</span><span className="wmap-dim">—</span>
              </div>
              {h.tokens.map(t => (
                <div key={t.mint} className="wmap-hrow tok" title={`${t.name || t.symbol || ''}\n${t.mint}\nclick to chart`}
                  onClick={() => navigateToken(t.mint)}>
                  <span className="wmap-sym">{t.symbol || short(t.mint)}</span>
                  <span>{fmtAmt(t.amount)}</span>
                  <span className={t.valueUsd == null ? 'wmap-dim' : ''}>{fmtUsd(t.valueUsd)}</span>
                  <span className="wmap-dim">{fmtUsd(t.mcapUsd)}</span>
                </div>
              ))}
              {h.tokenCount > h.tokens.length && <div className="wmap-dim wmap-pad">+{h.tokenCount - h.tokens.length} more (dust / unpriced)</div>}
            </div>
          )}

          <div className="wmap-section-head">
            <span>Interactions</span>
            <span className="wmap-dim">
              {g ? `${g.links.length} co-trader${g.links.length === 1 ? '' : 's'} · ${g.coins.length} coin${g.coins.length === 1 ? '' : 's'} · 72h${g.stats?.weak ? ' · few trades, weak links' : ''}${g.stats?.botLike ? ' · router/bot-like' : ''}` : graph.loading
                ? <span className="wmap-loading" role="status" aria-label="loading">loading<span className="wmap-dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span></span>
                : ''}
            </span>
            <span style={{ flex: 1 }} />
            <button className={`wmap-btn${views.d2 ? ' on' : ''}`} onClick={() => toggleView('d2')} title="2D Timeline / Clock of trades">2D</button>
            <button className={`wmap-btn${views.d3 ? ' on' : ''}`} onClick={() => toggleView('d3')} title="Rotating 3D map">3D</button>
            <button className="wmap-btn" disabled={fund.loading || !!fund.data}
              onClick={loadFunding} title="One-hop SOL/token transfers (latest 100)">
              {fund.loading ? 'Loading…' : fund.data ? `Funding: ${fund.data.counterparties.length}` : 'Load funding links'}
            </button>
          </div>
          {graph.err && <div className="wmap-err">{graph.err}</div>}
          {fund.err && <div className="wmap-err">{fund.err}</div>}
          <div style={views.d2 && views.d3 ? { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' } : undefined}>
          {views.d2 && (
            <div style={views.d3 ? { flex: '1 1 320px', minWidth: 0 } : undefined}>
            <RoutinesView center={addr} graph={g} holdings={h} pat={pat.data} nameOf={nameOf} currentMint={currentMint} highlight={hilite}
              onLoadPatterns={loadPatterns} patLoading={pat.loading}
              onWallet={(a) => setStack(s => (s[s.length - 1] === a ? s : [...s.slice(-19), a]))} />
            </div>
          )}
          {views.d3 && (
            <div style={views.d2 ? { flex: '1 1 320px', minWidth: 0 } : undefined}>
            <WalletGraph center={addr} holdings={h} graph={g} funding={fund.data} nameOf={nameOf} watchedSet={watchedSet}
              flat={false} groupOf={groupOf}
              onCoin={(m) => navigateToken(m)}
              onWallet={(a) => setStack(s => (s[s.length - 1] === a ? s : [...s.slice(-19), a]))} />
            </div>
          )}
          </div>
          <div className="wmap-legend">
            <span><i style={{ background: '#00cc6a' }} />held</span>
            <span><i style={{ border: '1px solid #565a63' }} />traded</span>
            <span><i style={{ background: '#c8cfdc' }} />co-trader</span>
            <span><i style={{ background: '#4a9eff' }} />funding</span>
            <span style={{ flex: 1 }} />
            {budget && <span className="wmap-dim" title="Shared daily data budget">budget {Math.round(100 * budget.remaining / Math.max(1, budget.limit))}%</span>}
          </div>

          <div className="wmap-section-head">
            <span>Patterns</span>
            <span className="wmap-dim">{pat.data ? `${pat.data.clusters.length} group${pat.data.clusters.length === 1 ? '' : 's'} · 24h · first buys within 2s on 3+ coins` : ''}</span>
            <span style={{ flex: 1 }} />
            <button className="wmap-btn" disabled={pat.loading} onClick={loadPatterns}
              title="Wallets that keep buying the same coins at the same moment (no credits)">
              {pat.loading ? 'Loading…' : pat.data ? '↻' : 'Find groups'}
            </button>
          </div>
          {pat.err && <div className="wmap-err">{pat.err}</div>}
          {pat.data && !pat.data.clusters.length && <div className="wmap-dim wmap-pad">No repeat groups for this wallet</div>}
          {pat.data?.clusters.map((c, i) => (
            <div key={i} className="wmap-pad" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 11 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <i style={{ width: 8, height: 8, borderRadius: '50%', border: `2px solid ${GROUP_COLORS[i % GROUP_COLORS.length]}`, flex: 'none' }} />
                <b style={{ color: '#ffb84a' }}>{c.score}</b>
                <span className="wmap-dim">{c.reasons.join(' · ')}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', marginTop: 2 }}>
                {c.wallets.map(w => (
                  <span key={w} title={w} style={{ cursor: 'pointer', color: w === addr ? '#00cc6a' : nameOf(w) ? '#4a9eff' : '#c8cfdc' }}
                    onClick={() => w !== addr && setStack(s => [...s.slice(-19), w])}>{nameOf(w) || short(w)}</span>
                ))}
              </div>
              <div className="wmap-dim" style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', marginTop: 2 }}>
                {c.coins.slice(0, 12).map(m => (
                  <span key={m} title={`${m}\nclick to chart`} style={{ cursor: 'pointer' }} onClick={() => navigateToken(m)}>{short(m)}</span>
                ))}
                {c.nCoins > 12 && <span>+{c.nCoins - 12}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
